/**
 * Endpoints #48-#50 — notifications (SPEC `notifications`).
 *
 * A notification is one event ("this post went viral"). The entity is deliberately source-agnostic:
 * `type` is an open vocabulary, `card` is the only post-shaped field, and every future source adds
 * a `type` plus its own state table without touching this contract or any surface built on it.
 *
 * Two halves live here: the `notifications` settings blob (§4.1 registry — the viral thresholds the
 * user edits in Settings) and the read models #48-#50 answer with.
 */
import { z } from 'zod';
import { AGGREGATE_METRIC_KEYS } from '../enums.js';
/* ------------------------------------------------------------------- types */
/**
 * `notifications.type`. Open by contract — the column carries no CHECK, so a future source ships a
 * new value without a migration. The DTO narrows to what this build can render: `viral_post` (a
 * tracked post crossed its thresholds) and `new_post` (New Post Watching caught a fresh post).
 */
export const NOTIFICATION_TYPES = ['viral_post', 'new_post'];
/**
 * `notification_mutes.type` (#86) — a **superset** of the row vocabulary above: every notification
 * type can be muted, plus `viral_post_watch`, a scoped variant that silences the viral pass only on
 * `trigger_source='watch'` runs (§6.5). `viral_post_watch` is a mute flavor and never a
 * `notifications` row value, which is why `NotificationType` stays narrow — it types
 * `notifications.insert`, so a leak into a written row is a compile error.
 */
export const NOTIFICATION_MUTE_TYPES = [...NOTIFICATION_TYPES, 'viral_post_watch'];
/**
 * `notification_deliveries.channel` — the push seam, and open for the same reason `type` is: a
 * second surface is one more value plus one more entry from the channel helper, with no migration
 * and no change to the check service. Two ship today, both Telegram: the viral bot (#52-#55) and
 * the watch bot (#73-#76), separate bindings so the two alert kinds can sound different.
 */
export const DELIVERY_CHANNELS = ['telegram', 'telegram_watch'];
/** `notification_deliveries.status`. `skipped` = the channel was disconnected before the send. */
export const DELIVERY_STATUSES = ['queued', 'sent', 'failed', 'skipped'];
/** One rule per metric; the five are independent and evaluated with AND semantics inside each. */
export const VIRAL_RULE_METRICS = AGGREGATE_METRIC_KEYS;
export const VIRAL_DEFAULT_MULTIPLIER = 5;
export const VIRAL_DEFAULT_REFIRE_GROWTH_PCT = 50;
/** Per-metric absolute floors of a fresh install (SPEC § Rules). */
export const VIRAL_DEFAULT_FLOORS = {
    reactions: 50,
    comments: 15,
    shares: 5,
    saves: 10,
    totalEngagement: 75,
};
export function notificationsSettingsDefault() {
    const rules = {};
    for (const metric of VIRAL_RULE_METRICS) {
        rules[metric] = {
            enabled: true,
            multiplier: VIRAL_DEFAULT_MULTIPLIER,
            floor: VIRAL_DEFAULT_FLOORS[metric],
        };
    }
    return { rules, refireGrowthPct: VIRAL_DEFAULT_REFIRE_GROWTH_PCT };
}
/** Frozen default for the §4.1 registry table; mutable copies come from the factory above. */
export const NOTIFICATIONS_SETTINGS_DEFAULT = notificationsSettingsDefault();
/** Bounds are wide on purpose: the user, not the schema, decides what "viral" means for them. */
export const VIRAL_MULTIPLIER_MAX = 1000;
export const VIRAL_FLOOR_MAX = 10_000_000;
export const VIRAL_REFIRE_GROWTH_PCT_MAX = 1000;
const ViralRuleConfigSchema = z.strictObject({
    enabled: z.boolean(),
    multiplier: z.number().min(1).max(VIRAL_MULTIPLIER_MAX),
    floor: z.number().int().min(0).max(VIRAL_FLOOR_MAX),
});
const ViralRulesSchema = z.strictObject({
    reactions: ViralRuleConfigSchema,
    comments: ViralRuleConfigSchema,
    shares: ViralRuleConfigSchema,
    saves: ViralRuleConfigSchema,
    totalEngagement: ViralRuleConfigSchema,
});
/**
 * Strict schema for the #21 write: the tab sends the whole blob, so a missing rule is a bug rather
 * than a partial edit (the *settings key* is what #21 patches, not the fields inside it).
 */
export const NotificationsSettingsSchema = z.strictObject({
    rules: ViralRulesSchema,
    refireGrowthPct: z.number().int().min(1).max(VIRAL_REFIRE_GROWTH_PCT_MAX),
});
const StoredViralRuleSchema = z.object({
    enabled: z.boolean().catch(true).default(true),
    multiplier: z.number().min(1).max(VIRAL_MULTIPLIER_MAX).catch(VIRAL_DEFAULT_MULTIPLIER),
    floor: z.number().int().min(0).max(VIRAL_FLOOR_MAX).catch(0),
});
/**
 * Lenient decode of the stored blob: every field falls back rather than throwing, so a hand-edited
 * database can never crash a boot (same contract as the other §4.1 validators). A missing rule
 * falls back to that metric's default, floor included.
 */
export function decodeNotificationsSettings(value) {
    const defaults = notificationsSettingsDefault();
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return defaults;
    const source = value;
    const rawRules = typeof source.rules === 'object' && source.rules !== null && !Array.isArray(source.rules)
        ? source.rules
        : {};
    const rules = {};
    for (const metric of VIRAL_RULE_METRICS) {
        const parsed = StoredViralRuleSchema.safeParse(rawRules[metric]);
        rules[metric] = parsed.success
            ? { ...parsed.data, floor: parsed.data.floor }
            : defaults.rules[metric];
    }
    const growth = z
        .number()
        .int()
        .min(1)
        .max(VIRAL_REFIRE_GROWTH_PCT_MAX)
        .safeParse(source.refireGrowthPct);
    return {
        rules,
        refireGrowthPct: growth.success ? growth.data : defaults.refireGrowthPct,
    };
}
export const NOTIFICATION_FILTERS = ['unread', 'all'];
export const MAX_NOTIFICATIONS_LIMIT = 30;
export const DEFAULT_NOTIFICATIONS_LIMIT = 20;
/**
 * #48. The cursor is opaque and echoes `filter` for the same reason #26's echoes `sort`: a keyset
 * predicate only means anything against the list it was cut from, so replaying one under the other
 * filter is a 400 rather than a silently wrong page.
 */
export const NotificationsQuerySchema = z.object({
    filter: z.enum(NOTIFICATION_FILTERS).default('unread'),
    cursor: z.string().max(200).optional(),
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(MAX_NOTIFICATIONS_LIMIT)
        .default(DEFAULT_NOTIFICATIONS_LIMIT),
});
/**
 * #86 body — an omitted key leaves that type untouched (the patch convention). `true` = enabled
 * (the mute row is deleted), `false` = disabled (a mute row is inserted).
 */
export const NotificationMutesBodySchema = z
    .strictObject({
    viralPost: z.boolean().optional(),
    newPost: z.boolean().optional(),
    viralPostWatch: z.boolean().optional(),
})
    .refine((body) => body.viralPost !== undefined ||
    body.newPost !== undefined ||
    body.viralPostWatch !== undefined, 'nothing to update');
//# sourceMappingURL=notifications.js.map